<template>
  <li>
    <button
      class="p-2 rounded-lg bg-fill-brand-secondary w-full text-start flex flex-row justify-between items-center gap-1 min-h-16"
      @click="emit('click', chatRoom.id)"
    >
      <div class="flex flex-col gap-1 ml-2">
        <span
          class="line-clamp-1 font-sans text-sm font-semibold leading-5 text-text"
        >
          # {{ chatRoomTitle }}
        </span>
        <div v-if="lastMessage" class="flex flex-row gap-0 items-center">
          <Avatar :user="lastMessage?.author" size="xs" class="mr-2" />
          <span
            class="text-text] font-sans font-normal leading-5 line-clamp-1 text-[13px] break-good"
          >
            {{ lastMessage.messageTranslation }}
          </span>
        </div>
      </div>
      <div class="flex flex-row gap-1 items-center">
        <div
          v-if="pendingMessages > 0"
          class="rounded-full bg-fill-critical font-serif text-xs aspect-square shrink-0 h-6 text-center flex items-center justify-center"
        >
          {{ pendingMessages }}
        </div>
        <Icon
          icon="weui:arrow-outlined"
          class="text-lg size-6 text-icon shrink-0"
        />
      </div>
    </button>
  </li>
</template>

<script setup lang="ts">
import { ChatRoom } from "@/stores/chat/model/chat-room";
import { computed, type UnwrapNestedRefs } from "vue";
import { Icon } from "@iconify/vue";
import Avatar from "../me/avatar.vue";
import { useObjectTranslation } from "@/stores/translation.store";

const emit = defineEmits<{
  click: [roomId: string];
}>();

const props = defineProps<{
  chatRoom: UnwrapNestedRefs<ChatRoom>;
}>();

const lastMessage = computed(() => {
  const messages = props.chatRoom.chatMessages;

  if (!messages) return null;

  for (const msg of messages.tailIterator()) {
    return msg;
  }

  return null;
});

const pendingMessages = computed(() => {
  let count = 0;

  const messages = props.chatRoom.chatMessages;
  if (!messages) return count;

  if (props.chatRoom.lastSeenMessageId === null) {
    return messages.size();
  }

  for (const message of messages.tailIterator()) {
    if (message.id === props.chatRoom.lastSeenMessageId) break;

    count++;
  }

  return count;
});

const { text: chatRoomTitle } = useObjectTranslation(
  computed(() => {
    const chatRoom = props.chatRoom;
    if (!chatRoom || !chatRoom.title) return null;

    return {
      authorUserId: undefined,
      originalText: chatRoom.title,
      objectAccessors: { objectId: chatRoom.id, objectProperty: "title" },
      translatedText: chatRoom.titleTranslation,
    };
  }),
);
</script>
